import { FC, useEffect, useState } from 'react';
import { coreService } from 'network-utils';
import CertificatePopupContainer from '@components/home/certificates/certificatePopupContainer';
import { useMutation } from 'react-query';
import moment from 'moment';
import { Avatar, MultiChoice } from 'learning-panel-components';
import { selectToken, selectUserNickname } from '@redux/slices/userSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '@redux/slices/informationSlice';
import styles from './quizQuestionsPlayer.module.scss';
import QuizBar from './quizBar/quizBar';
import QuizTopBar from './topBar/topBar';
import QuizPopup from './popups/popup';
import QuizExitPopup from './popups/quizExitPopup';

const QuizQuestionsPlayer: FC<QuizQuestionsPlayerProps> = (props: QuizQuestionsPlayerProps) => {
    const { isRetakeEnabled, onRetake, quizQuestion, quizInfo, setQuizQuestion, userAvatar } = props;
    const services = coreService.quizApi;
    const [isQuestionSubmitted, setIsQuestionSubmitted] = useState<boolean>(false);
    const token = useSelector(selectToken);
    const nickname = useSelector(selectUserNickname);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isPassed, setIsPassed] = useState(false);
    const dispatch = useDispatch();
    const [userAnswers, setUserAnswers] = useState([]);
    const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);
    const [mistakesStatuses, setMistakesStatuses] = useState(
        Object.fromEntries(Array.from({ length: quizInfo.incorrectQuestionsAllowed }).map((_, index) => [index, false]))
    );
    const [questionStatuses, setQuestionStatuses] = useState<{ key: number; value: string }[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    const [showQuitPopup, setShowQuitPopup] = useState<boolean>(false);
    const [questionInfo, setQuestionInfo] = useState<coreService.QuestionInfo | undefined>();
    const [questionSubmitResponse, setQuestionSubmitResponse] = useState<
        coreService.QuestionSubmitResponse | undefined
    >();

    const navigate = useNavigate();
    useEffect(() => {
        const questionId = quizQuestion?.questionId;
        const quizProgressId = quizQuestion?.quizProgressId;

        if (questionId && quizProgressId) {
            onStartQuestion(questionId, quizProgressId);
        }
    }, []);

    useEffect(() => {
        if (!quizQuestion || !questionSubmitResponse?.questionId) return;
        setIsQuestionSubmitted(false);
        onStartQuestion(questionSubmitResponse?.questionId, quizQuestion?.quizProgressId);
        setUserAnswers([]);
        setCorrectAnswers([]);
    }, [currentQuestionIndex]);

    const onSubmit = (): void => {
        setIsQuestionSubmitted(true);
        const userAnswer = userAnswers[0];
        const quizProgressId = quizQuestion?.quizProgressId;
        const questionId = quizQuestion?.questionId;
        if (!quizProgressId || !questionId) return;
        submitMutation({ questionId, quizProgressId, userAnswer });
    };

    const submitQuestion = async (input: {
        questionId: number;
        quizProgressId: number;
        userAnswer: number;
    }): Promise<{
        success: boolean;
        isCorrect: boolean;
        correctAnswer: number;
        questionId: number;
        isLastQuestion: boolean;
    }> => {
        const result = await services.submit(token, input);
        return result;
    };

    const { mutate: submitMutation } = useMutation(submitQuestion, {
        onSuccess: (result: {
            success: boolean;
            isCorrect: boolean;
            correctAnswer: number;
            questionId: number;
            isLastQuestion: boolean;
        }) => {
            if (result.success) {
                setQuizQuestion({
                    questionId: result.questionId,
                    quizProgressId: quizQuestion?.quizProgressId,
                    numberOfQuestions: quizInfo.numberOfQuestions
                });
                setQuestionSubmitResponse(result);
                setCorrectAnswers([...correctAnswers, String(result.correctAnswer)]);
                if (!result.isCorrect) {
                    const newMistakesStatuses = {
                        ...mistakesStatuses,
                        [currentQuestionIndex]: true
                    };
                    setMistakesStatuses(newMistakesStatuses);

                    const mistakesCount = Object.values(newMistakesStatuses).filter(value => value).length;
                    if (mistakesCount === quizInfo.incorrectQuestionsAllowed) {
                        setIsPassed(false);
                        setIsPopupOpen(true);
                    }

                    setQuestionStatuses([...questionStatuses, { key: currentQuestionIndex, value: 'incorrect' }]);
                } else {
                    if (result.isLastQuestion) {
                        setIsPassed(true);
                        setIsPopupOpen(true);
                    }
                    setQuestionStatuses([...questionStatuses, { key: currentQuestionIndex, value: 'correct' }]);
                }
            }
        },
        onError: () => {
            dispatch(
                showAlert({
                    value: 'FailedFetchQuestion',
                    status: 'error',
                    title: '',
                    isAdmin: true
                })
            );
        }
    });

    const onStartQuestion = (questionId: number, quizProgressId: number): void => {
        startMutation({ questionId, quizProgressId });
    };

    const startQuestion = async (input: {
        questionId: number;
        quizProgressId: number;
    }): Promise<{
        success: boolean;
        question: any;
        answers: string[];
    }> => {
        const result = await services.questionStart(token, input);
        return result;
    };

    const { mutate: startMutation } = useMutation(startQuestion, {
        onSuccess: (result: { success: boolean; question: any; answers: string[] }) => {
            if (result.success) {
                setQuestionInfo({ question: result.question, answers: result.answers.filter(Boolean) });
            }
        },
        onError: () => {
            dispatch(
                showAlert({
                    value: 'FailedFetchQuestion',
                    status: 'error',
                    title: '',
                    isAdmin: true
                })
            );
        }
    });

    const handleOnSubmitOrNext = (): void => {
        if (isQuestionSubmitted) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            onSubmit();
        }
    };

    return (
        <div className={styles['quiz-questions-wrapper']}>
            {isPopupOpen && (
                <>
                    {!isPassed && (
                        <QuizPopup
                            setIsPopupOpen={setIsPopupOpen}
                            numOfAlowedMistakes={quizInfo.incorrectQuestionsAllowed}
                            numberOfAttempts={quizInfo.attemptsAllowed}
                            isRetakeEnabled={isRetakeEnabled}
                            onRetake={onRetake}
                        />
                    )}
                    {isPassed && (
                        <CertificatePopupContainer
                            isCustom={false}
                            onClose={() => {
                                setIsPopupOpen(false);
                                navigate('/app/home');
                            }}
                            isSkillTree={false}
                            isLearningPath={false}
                            inputCertificateData={{
                                name: nickname,
                                completedAt: moment().format('DD-MM-YYYY'),
                                entityDisplayName: quizInfo.courseDisplayName
                            }}
                        />
                    )}
                </>
            )}
            <div className={styles.wrapper}>
                {showQuitPopup && <QuizExitPopup setIsPopupOpen={setShowQuitPopup} />}
                <QuizTopBar
                    name={quizInfo.courseDisplayName}
                    mistakesStatuses={mistakesStatuses}
                    setShowQuitPopup={setShowQuitPopup}
                />
                <QuizBar
                    totalQuestions={quizInfo.numberOfQuestions}
                    currentQuestionIndex={currentQuestionIndex}
                    questionStatuses={questionStatuses}
                    profilePictureUrl={quizInfo.userProfilePicture}
                    nickname={nickname}
                />
                <MultiChoice
                    isQuestionSubmitted={isQuestionSubmitted}
                    onSubmit={handleOnSubmitOrNext}
                    currentQuestion={{
                        description: questionInfo?.question || '',
                        type: 'single',
                        data: {
                            answers: questionInfo?.answers || []
                        },
                        correctAnswers,
                        userAnswers,
                        setUserAnswers
                    }}
                    questionType={'multichoice'}
                    answerTypeText={'You can choose only one answer'}
                    tooltipText={'Your Answer'}
                    customWidth="90rem"
                    titleColor="#F178AE"
                    submitText={isQuestionSubmitted ? 'Next' : 'Submit'}
                    lowerCase={true}
                    isQuiz={true}
                    isQuizNextDisabled={!isPassed && isPopupOpen}
                    selectQuestionComponent={<Avatar name={nickname} avatarSrc={userAvatar} />}
                />
            </div>
        </div>
    );
};

interface QuizQuestionsPlayerProps {
    name: string;
    isRetakeEnabled: boolean;
    quizQuestion: coreService.QuizQuestion | undefined;
    setQuizQuestion: Function;
    quizInfo: coreService.QuizInfo;
    onRetake: Function;
    userAvatar?: string;
}

export default QuizQuestionsPlayer;
